import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageContainer } from '../../styles/CommonStyles';
import { useRbac } from '../../hooks/useRbac';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import { upgradeUsageVisibility } from '../../services/rbacService';
import MembersList from './MembersList';
import RolesList from './RolesList';
import InvitationsList from './InvitationsList';
import UsageLimitsPanel from './UsageLimitsPanel';
import GlobalFooter from '../GlobalFooter';

// ── Styled components ────────────────────────────────────────────────────────

const Container = styled(PageContainer)`
  max-width: 1100px;
  padding: 2.5rem;
  padding-bottom: 4rem;

  h1 {
    font-family: 'Space Grotesk', sans-serif;
    color: var(--brand-black);
    margin: 0 0 0.5rem;
    font-size: 1.75rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    text-transform: lowercase;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 0.9375rem;
  margin: 0 0 2rem;
  line-height: 1.5;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid var(--brand-line);
  margin-bottom: 1.75rem;
  gap: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${props => (props.$active ? 'var(--brand-gold)' : 'transparent')};
  color: ${props => (props.$active ? 'var(--brand-gold)' : '#666')};
  font-size: 0.9375rem;
  font-weight: ${props => (props.$active ? '600' : '400')};
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -1px;
  text-transform: lowercase;
  letter-spacing: 0.01em;

  &:hover {
    color: var(--brand-gold);
  }

  &:focus {
    outline: none;
  }
`;

const AccessDenied = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;

  h2 {
    font-size: 1.25rem;
    font-weight: 500;
    margin: 0 0 0.75rem;
    text-transform: lowercase;
  }

  p {
    font-size: 0.9375rem;
    margin: 0;
  }
`;

const RetryButton = styled.button`
  margin-top: 1.25rem;
  padding: 0.5rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  background: var(--brand-gold);
  color: var(--brand-black);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-transform: lowercase;
  transition: background 0.2s ease;

  &:hover {
    background: #b8973f;
  }

  &:focus {
    outline: none;
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

type TabKey = 'members' | 'roles' | 'invitations' | 'limits';

const TeamRolesPage: React.FC = () => {
  const { hasPermission, loading, error, refetch, isOwner } = useRbac();
  const { canViewLimits, canManageLimits, canDelegateLimits } = useUsageVisibility();
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const navigate = useNavigate();

  // If Firebase session expired, redirect to auth to re-establish it
  useEffect(() => {
    if (error === 'session_expired') {
      navigate('/auth?returnUrl=/settings/team', { replace: true });
    }
  }, [error, navigate]);

  // Universal roster access (owner directive 2026-08-16): EVERY member gets
  // at least the members tab — the server serves a names+roles roster without
  // team.view (emails and management need the permission). The invitations
  // and full-detail views keep their gates.
  const canViewMembers = true;
  const canViewFullMembers = hasPermission('team.view');
  const canViewRoles = hasPermission('roles.view');
  const canInvite = hasPermission('team.invite');

  // ORG-USAGE-2026 grandfather migration. Idempotent and additive: it unions
  // the current platform role templates into this account's roles (so existing
  // members keep everything they can see today and owners/admins pick up the
  // new administration keys), then stamps the account so the visibility gates
  // switch from fail-open to governed. Runs once per owner visit, in the
  // background — a failure is silent because the fail-open gates mean nothing
  // is broken if it never runs.
  useEffect(() => {
    if (loading || error || !isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await upgradeUsageVisibility();
        if (!cancelled && res?.status === 'upgraded') refetch();
      } catch {
        /* non-fatal by design — see comment above */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, isOwner]);

  if (loading || error === 'session_expired') {
    return (
      <Container>
        <div style={{ padding: '3rem 0', color: '#999', textAlign: 'center' }}>loading…</div>
      </Container>
    );
  }

  // A real resolution failure (transient network, a browser extension blocking
  // the Firestore Listen channel, a rules hiccup) must NOT masquerade as a
  // genuine "access restricted" — that dead-ends a legitimate owner with no
  // recourse. Surface a retry instead. `error` is null when reads succeeded
  // but the caller is genuinely not a member/owner, so the access-restricted
  // wall below still shows for true no-access.
  if (error) {
    return (
      <Container>
        <AccessDenied>
          <h2>couldn't load team settings</h2>
          <p>we ran into a problem loading your team permissions. this is usually temporary.</p>
          <RetryButton onClick={() => refetch()}>retry</RetryButton>
        </AccessDenied>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <h1>team & roles</h1>
        <Subtitle>
          manage your team members, permissions, and custom roles.{' '}
          <a
            href="/organization"
            style={{ color: '#7A5C00', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            organization details →
          </a>
        </Subtitle>

        <TabBar>
          {canViewMembers && (
            <Tab $active={activeTab === 'members'} onClick={() => setActiveTab('members')}>
              members
            </Tab>
          )}
          {canViewRoles && (
            <Tab $active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
              roles
            </Tab>
          )}
          {canViewFullMembers && (
            <Tab
              $active={activeTab === 'invitations'}
              onClick={() => setActiveTab('invitations')}
            >
              invitations
            </Tab>
          )}
          {canViewLimits && (
            <Tab $active={activeTab === 'limits'} onClick={() => setActiveTab('limits')}>
              usage limits
            </Tab>
          )}
        </TabBar>

        {activeTab === 'members' && canViewMembers && (
          <MembersList onManageInvitations={() => setActiveTab('invitations')} />
        )}
        {activeTab === 'roles' && canViewRoles && <RolesList />}
        {activeTab === 'invitations' && canViewFullMembers && (
          <InvitationsList canInvite={canInvite} />
        )}
        {activeTab === 'limits' && canViewLimits && (
          <UsageLimitsPanel canManage={canManageLimits} canDelegate={canDelegateLimits} />
        )}
      </Container>
      <GlobalFooter />
    </>
  );
};

export default TeamRolesPage;
